import { TOKEN_MESSAGES } from '@fos/shared/contracts/tokens';
import type { ApiTokenAccess } from '@fos/shared/enums';
import {
  API_TOKEN_ACCESS_DESCRIPTIONS,
  API_TOKEN_ACCESS_LABELS,
  API_TOKEN_LOCAL_RUNNER_LABEL,
  API_TOKEN_LOCAL_RUNNER_RISK,
} from '@fos/shared/labels';
import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { Copy, X } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Combobox } from '../../../ui/Combobox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { SwitchRow } from '../team/components/SwitchRow';
import {
  connectCommand,
  DEFAULT_EXPIRY,
  EXPIRY_CHOICES,
  type ExpiryChoice,
  expiryChoiceLabel,
  tokenSummary,
} from './tokens-ui';

type ScopeMode = 'all' | 'selected';
type FieldErrors = Partial<Record<'name' | 'scope' | 'expiresOn', string>>;

interface FormState {
  name: string;
  access: ApiTokenAccess;
  scope: ScopeMode;
  clientIds: string[];
  projectIds: string[];
  expiry: ExpiryChoice;
  expiresOn: string;
  /** „Może zlecać pracę na moim komputerze” — tylko przy poziomie „Odczyt i zapis”, domyślnie wyłączone */
  localRunner: boolean;
}

const INITIAL: FormState = {
  name: '',
  access: 'read',
  scope: 'all',
  clientIds: [],
  projectIds: [],
  expiry: DEFAULT_EXPIRY,
  expiresOn: '',
  localRunner: false,
};

const ACCESS_OPTIONS = (['read', 'write'] as const).map((v) => ({ value: v, label: API_TOKEN_ACCESS_LABELS[v] }));
const SCOPE_OPTIONS = [
  { value: 'all' as const, label: 'Wszystko' },
  { value: 'selected' as const, label: 'Wybrani klienci i projekty' },
];
const EXPIRY_OPTIONS = EXPIRY_CHOICES.map((c) => ({ value: c, label: expiryChoiceLabel(c) }));

/** Wynik utworzenia (surowy token tylko w pamięci dialogu, do zamknięcia). */
interface Created {
  secret: string;
  summary: string;
  name: string;
}

/**
 * „Nowy token” (Ustawienia → Tokeny i agenci; czeka na projekt w Figmie): nazwa, poziom (odczyt / zapis), przy zapisie
 * przełącznik „Może zlecać pracę na moim komputerze” z opisem ryzyka (Fala 6, domyślnie wyłączony), zakres (wszystko
 * albo wybrani klienci i projekty), ważność (30 / 90 dni, rok, własna data). Utworzenie wymaga potwierdzenia
 * hasłem (step-up obsługuje link tRPC). Po utworzeniu ten sam dialog pokazuje surowy token RAZ, z gotowym poleceniem
 * dla Claude Code; zamknięcie czyści token z pamięci.
 */
export function NewTokenDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [created, setCreated] = useState<Created | null>(null);

  useEffect(() => {
    if (!open) setCreated(null);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={created ? 'Token utworzony' : 'Nowy token'}
      className={FULLSCREEN_ON_PHONE}
      description={
        created
          ? `„${created.name}” — ${created.summary}`
          : 'Token działa z Twoimi uprawnieniami, zawężonymi do zakresu. Każdy agent powinien mieć własny token.'
      }
    >
      {created ? (
        <CreatedView created={created} onDone={() => onOpenChange(false)} />
      ) : (
        <TokenForm open={open} onCancel={() => onOpenChange(false)} onCreated={setCreated} />
      )}
    </Dialog>
  );
}

function TokenForm({
  open,
  onCancel,
  onCreated,
}: {
  open: boolean;
  onCancel: () => void;
  onCreated: (c: Created) => void;
}) {
  const utils = trpc.useUtils();
  const create = trpc.tokens.create.useMutation();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);
  const options = trpc.tokens.scopeOptions.useQuery(undefined, {
    enabled: open && form.scope === 'selected',
    staleTime: 60_000,
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const clients = options.data?.clients ?? [];
  const projects = options.data?.projects ?? [];
  const pickedClients = form.clientIds.map((id) => ({ id, label: clients.find((c) => c.id === id)?.name ?? '…' }));
  const pickedProjects = form.projectIds.map((id) => {
    const p = projects.find((x) => x.id === id);
    return { id, label: p ? (p.clientName ? `${p.name} · ${p.clientName}` : p.name) : '…' };
  });
  const clientOptions = useMemo(
    () => clients.filter((c) => !form.clientIds.includes(c.id)).map((c) => ({ value: c.id, label: c.name })),
    [clients, form.clientIds],
  );
  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => !form.projectIds.includes(p.id))
        .map((p) => ({ value: p.id, label: p.name, ...(p.clientName ? { description: p.clientName } : {}) })),
    [projects, form.projectIds],
  );

  const today = todayLocal();
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const next: FieldErrors = {};
    if (!form.name.trim()) next.name = 'Podaj nazwę, np. „Claude Code — laptop”';
    if (form.scope === 'selected' && form.clientIds.length + form.projectIds.length === 0) {
      next.scope = 'Wybierz co najmniej jednego klienta albo projekt';
    }
    if (form.expiry === 'custom') {
      if (!form.expiresOn) next.expiresOn = 'Wybierz datę';
      else if (form.expiresOn < today) next.expiresOn = 'Data musi być w przyszłości';
      else if (form.expiresOn > addDaysLocal(today, 365)) next.expiresOn = 'Najdłużej rok';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    try {
      const res = await create.mutateAsync({
        name: form.name.trim(),
        access: form.access,
        scope:
          form.scope === 'all' ? { all: true } : { all: false, clientIds: form.clientIds, projectIds: form.projectIds },
        ...(form.expiry === 'custom' ? { expiresOn: form.expiresOn } : { expiresInDays: Number(form.expiry) }),
        localRunnerAllowed: form.access === 'write' && form.localRunner,
      });
      void utils.tokens.list.invalidate();
      setForm(INITIAL);
      onCreated({ secret: res.secret, summary: tokenSummary(res.token), name: res.token.name });
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return; // anulowane potwierdzenie hasłem
      const mapped: FieldErrors = {};
      for (const issue of validationIssues(err)) {
        if (issue.path === 'name' || issue.path === 'expiresOn') mapped[issue.path] = issue.message;
        else if (issue.path.startsWith('scope')) mapped.scope = issue.message;
      }
      if (Object.keys(mapped).length > 0) setErrors(mapped);
      else setFormError(errorMessage(err));
    }
  };

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-5" aria-label="Nowy token">
      <Field label="Nazwa" error={errors.name}>
        <Input
          autoFocus
          autoComplete="off"
          maxLength={80}
          placeholder="np. Claude Code — laptop"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
        />
      </Field>

      <div className="flex flex-col gap-2">
        <Label>Poziom</Label>
        <Segmented
          aria-label="Poziom"
          value={form.access}
          onValueChange={(v) => setForm((f) => ({ ...f, access: v, localRunner: v === 'write' && f.localRunner }))}
          options={ACCESS_OPTIONS}
          className="self-start"
        />
        <p className="text-caption text-fg-muted">{API_TOKEN_ACCESS_DESCRIPTIONS[form.access]}</p>
        {form.access === 'write' && (
          <SwitchRow
            checked={form.localRunner}
            onCheckedChange={(on) => set('localRunner', on)}
            label={API_TOKEN_LOCAL_RUNNER_LABEL}
            description={API_TOKEN_LOCAL_RUNNER_RISK}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label>Zakres</Label>
        <Segmented
          aria-label="Zakres"
          value={form.scope}
          onValueChange={(v) => set('scope', v)}
          options={SCOPE_OPTIONS}
          className="self-start"
        />
        {form.scope === 'all' ? (
          <p className="text-caption text-fg-muted">Wszystko, do czego masz dostęp — także Twoje zadania wewnętrzne.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <PickedList
              label="Klienci"
              items={pickedClients}
              onRemove={(id) =>
                set(
                  'clientIds',
                  form.clientIds.filter((x) => x !== id),
                )
              }
            />
            <Combobox
              aria-label="Dodaj klienta"
              value={null}
              placeholder={options.isPending ? 'Wczytywanie…' : 'Dodaj klienta'}
              searchPlaceholder="Szukaj klienta…"
              emptyText="Brak klientów"
              options={clientOptions}
              disabled={options.isPending}
              onValueChange={(id) => set('clientIds', [...form.clientIds, id])}
            />
            <PickedList
              label="Projekty"
              items={pickedProjects}
              onRemove={(id) =>
                set(
                  'projectIds',
                  form.projectIds.filter((x) => x !== id),
                )
              }
            />
            <Combobox
              aria-label="Dodaj projekt"
              value={null}
              placeholder={options.isPending ? 'Wczytywanie…' : 'Dodaj projekt'}
              searchPlaceholder="Szukaj projektu…"
              emptyText="Brak projektów"
              options={projectOptions}
              disabled={options.isPending}
              onValueChange={(id) => set('projectIds', [...form.projectIds, id])}
            />
            {options.isError && <FormMessage>Nie udało się wczytać klientów i projektów.</FormMessage>}
            {errors.scope && (
              <p role="alert" className="text-caption text-danger">
                {errors.scope}
              </p>
            )}
            <p className="text-caption text-fg-muted">
              Wybrany projekt daje dostęp tylko do niego — bez innych projektów tego klienta. Token z zakresem nie widzi
              Twoich zadań wewnętrznych.
            </p>
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Ważność">
          <Select
            value={form.expiry}
            onValueChange={(v) => set('expiry', v)}
            options={EXPIRY_OPTIONS}
            aria-label="Ważność"
          />
        </Field>
        {form.expiry === 'custom' && (
          <Field label="Wygasa po dniu" error={errors.expiresOn}>
            <DateInput value={form.expiresOn} onValueChange={(v) => set('expiresOn', v)} />
          </Field>
        )}
      </div>

      {formError && <FormMessage>{formError}</FormMessage>}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel}>
          Anuluj
        </Button>
        <Button variant="primary" type="submit" loading={create.isPending}>
          Utwórz token
        </Button>
      </div>
    </form>
  );
}

export function PickedList({
  label,
  items,
  onRemove,
}: {
  label: string;
  items: { id: string; label: string }[];
  onRemove: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={label}>
      {items.map((item) => (
        <li
          key={item.id}
          className="inline-flex h-7 items-center gap-1.5 rounded-sm bg-elevated pr-1 pl-2 text-caption font-medium text-fg"
        >
          <span className="max-w-56 truncate">{item.label}</span>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={`Usuń: ${item.label}`}
            className="inline-flex size-5 items-center justify-center rounded-xs text-fg-muted hover:bg-line hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
          >
            <X size={12} strokeWidth={2} aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

async function copyText(text: string, done: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(done);
  } catch {
    toast.error('Nie udało się skopiować. Zaznacz tekst i skopiuj ręcznie.');
  }
}

function CreatedView({ created, onDone }: { created: Created; onDone: () => void }) {
  const command = connectCommand(created.secret);
  return (
    <div className="flex flex-col gap-5">
      <ContextBanner tone="warning">{TOKEN_MESSAGES.showOnce}</ContextBanner>
      <div className="flex flex-col gap-2">
        <Label>Token</Label>
        <div className="flex items-center gap-2">
          <Mono
            aria-label="Token"
            className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-2 text-caption break-all text-fg select-all"
          >
            {created.secret}
          </Mono>
          <Button
            variant="secondary"
            icon={Copy}
            aria-label="Kopiuj token"
            onClick={() => void copyText(created.secret, 'Token skopiowany')}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label>Claude Code — polecenie w terminalu</Label>
        <div className="flex items-start gap-2">
          <Mono
            aria-label="Polecenie"
            className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-2 text-caption break-all text-fg select-all"
          >
            {command}
          </Mono>
          <Button
            variant="secondary"
            icon={Copy}
            aria-label="Kopiuj polecenie"
            onClick={() => void copyText(command, 'Polecenie skopiowane')}
          />
        </div>
        <p className="text-caption text-fg-muted">
          Sprawdzenie: <Mono>claude mcp list</Mono>, w sesji <Mono>/mcp</Mono>. Nie wklejaj tokenu do repozytorium.
        </p>
      </div>
      <div className="flex justify-end pt-1">
        <Button variant="primary" onClick={onDone}>
          Gotowe
        </Button>
      </div>
    </div>
  );
}
